import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import { getProject } from "../services/projectService";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Icon } from 'leaflet';
import { Project } from "../interfaces/Project";
import {
    Box,
    Card,
    Typography,
    Grid,
    Skeleton,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper
} from '@mui/material';

const customIcon = new Icon({
    iconUrl: 'https://leafletjs.com/examples/custom-icons/leaf-green.png',
    iconSize: [38, 38],
});

const ConsultationProjectForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [projet, setProject] = useState<Project>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchDetailProjects = async () => {
            try {
                setLoading(true);
                const project = await getProject(Number(id));
                setProject(project);
                setError(null);
            } catch (error) {
                setError('Erreur lors de la récupération des informations du projet');
                console.error('Erreur:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchDetailProjects();
    }, [id]);

    const InfoSection: React.FC<{ title: string; data: { label: string; value: string | number }[] }> = ({ title, data }) => (
        <Card className="bg-white/80 backdrop-blur-sm p-6">
            <Typography variant="h6" className="text-emerald-800 font-semibold mb-4">
                {title}
            </Typography>
            <TableContainer component={Paper} elevation={0} className="bg-transparent">
                <Table>
                    <TableBody>
                        {data.map((item, index) => (
                            <TableRow 
                                key={index}
                                className="hover:bg-emerald-50/50 transition-colors"
                            >
                                <TableCell 
                                    component="th" 
                                    scope="row"
                                    className="font-medium text-emerald-700 w-1/3"
                                >
                                    {item.label}
                                </TableCell>
                                <TableCell className="text-gray-700">
                                    {loading ? (
                                        <Skeleton width={100} />
                                    ) : (
                                        item.value || '-'
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );

    return (
        <MainLayout>
            <Box className="p-4 sm:p-6 md:p-8 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Typography variant="h4" className="text-emerald-800 font-bold mb-2">
                        {loading ? (
                            <Skeleton width={300} />
                        ) : (
                            projet ? `Projet : ${projet.libelle}` : 'Projet non trouvé'
                        )}
                    </Typography>
                    {error && (
                        <Alert 
                            severity="error" 
                            className="mt-4"
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}
                </Card>

                {/* Contenu principal */}
                <Grid container spacing={3}>
                    {/* Carte */}
                    {projet?.location && (
                        <Grid item xs={12}>
                            <Card className="bg-white/80 backdrop-blur-sm p-6">
                                <Typography variant="h6" className="text-emerald-800 font-semibold mb-4">
                                    Géolocalisation
                                </Typography>
                                <div className="h-[400px] rounded-lg overflow-hidden">
                                    <MapContainer
                                        center={[projet.location.lat, projet.location.lng]}
                                        zoom={13}
                                        style={{ height: '100%', width: '100%' }}
                                    >
                                        <TileLayer
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                        />
                                        <Marker
                                            position={[projet.location.lat, projet.location.lng]}
                                            icon={customIcon}
                                        >
                                            <Popup>{projet.libelle}</Popup>
                                        </Marker>
                                    </MapContainer>
                                </div>
                            </Card>
                        </Grid>
                    )}

                    {/* Informations générales */}
                    <Grid item xs={12} md={6}>
                        <InfoSection
                            title="Informations générales"
                            data={[
                                { label: "Adresse", value: projet?.address || '' },
                                { label: "Ville", value: projet?.city || '' },
                                { label: "Code postal", value: projet?.postalCode || '' },
                                { label: "Typologie", value: projet?.type || '' },
                                { label: "Surface au sol", value: projet?.groundSurface || '' },
                                { label: "Surface habitable", value: projet?.livingSurface || '' },
                                { label: "Altitude", value: projet?.altitude || '' },
                                { label: "Zone climatique", value: projet?.climateZone || '' },
                                { label: "Energie", value: projet?.energy || '' },
                            ]}
                        />
                    </Grid>

                    {/* Informations énergétiques */}
                    <Grid item xs={12} md={6}>
                        <InfoSection
                            title="Informations énergétiques"
                            data={[
                                { label: "Consommation", value: projet?.energyConsumption || '' },
                                { label: "Gaz à Effet de Serre", value: projet?.greenhouseGas || '' },
                                { label: "Apports gratuits", value: projet?.freeContributions || '' },
                                { label: "Classe énergétique", value: projet?.energyClass || '' },
                            ]}
                        />
                    </Grid>

                    {/* Déperdition énergétique */}
                    <Grid item xs={12} md={6}>
                        <InfoSection
                            title="Déperdition énergétique"
                            data={[
                                { label: "Renouvellement de l'air", value: projet?.airRenewal || '' },
                                { label: "Murs", value: projet?.walls || '' },
                                { label: "Ponts thermiques", value: projet?.thermalBridges || '' },
                                { label: "Planchers hauts", value: projet?.upperFloors || '' },
                                { label: "Menuiseries", value: projet?.woodwork || '' },
                                { label: "Planchers bas", value: projet?.lowerFloors || '' },
                            ]}
                        />
                    </Grid>

                    {/* Informations techniques */}
                    <Grid item xs={12} md={6}>
                        <InfoSection
                            title="Informations techniques"
                            data={[
                                { label: "Surface (m²)", value: projet?.technicalInfo?.area || '' },
                                { label: "Hauteur (m)", value: projet?.technicalInfo?.height || '' },
                                { label: "Nombre d'étages", value: projet?.technicalInfo?.floors || '' },
                                { label: "Année de construction", value: projet?.technicalInfo?.yearBuilt || '' },
                            ]}
                        />
                    </Grid>
                </Grid>
            </Box>
        </MainLayout>
    );
};

export default ConsultationProjectForm;
